export type ArticleMediaLayout = "left" | "center" | "right" | "full";
export type ArticleImageLayout = ArticleMediaLayout | "wide";
export type ArticleMediaSize = "sm" | "md" | "lg" | "full";

export const ARTICLE_MEDIA_MIN_WIDTH = 200;
export const ARTICLE_MEDIA_MAX_WIDTH = 920;
export const ARTICLE_MEDIA_DEFAULT_WIDTH = 560;
export const ARTICLE_MEDIA_OFFSET_X_MIN = -18;
export const ARTICLE_MEDIA_OFFSET_X_MAX = 18;
export const ARTICLE_MEDIA_OFFSET_Y_MIN = -160;
export const ARTICLE_MEDIA_OFFSET_Y_MAX = 160;

export function safeArticleMediaLayout(input: unknown): ArticleMediaLayout {
  return input === "left" || input === "right" || input === "full" ? input : "center";
}

export function safeArticleImageLayout(input: unknown): ArticleImageLayout {
  return input === "wide" || input === "left" || input === "right" || input === "center" ? input : "full";
}

export function clampArticleMediaWidth(input: unknown, fallback: number | null = ARTICLE_MEDIA_DEFAULT_WIDTH) {
  if (input === null) return null;
  const width = input === undefined ? fallback : Number(input);
  if (width === null) return null;
  if (!Number.isFinite(width)) return fallback;
  return Math.max(ARTICLE_MEDIA_MIN_WIDTH, Math.min(ARTICLE_MEDIA_MAX_WIDTH, Math.round(width)));
}

export function articleMediaWidthFromLegacySize(input: unknown) {
  if (input === "sm") return 420;
  if (input === "md") return 560;
  if (input === "lg") return 720;
  if (input === "full") return null;
  return ARTICLE_MEDIA_DEFAULT_WIDTH;
}

export function articleImageWidthFromSize(size: ArticleMediaSize) {
  if (size === "sm") return 42;
  if (size === "md") return 58;
  if (size === "lg") return 74;
  return 100;
}

export function clampArticleMediaOffsetX(input: unknown, fallback = 0) {
  const offset = input === undefined ? fallback : Number(input);
  if (!Number.isFinite(offset)) return fallback;
  return Math.max(ARTICLE_MEDIA_OFFSET_X_MIN, Math.min(ARTICLE_MEDIA_OFFSET_X_MAX, Math.round(offset)));
}

export function clampArticleMediaOffsetY(input: unknown, fallback = 0) {
  const offset = input === undefined ? fallback : Number(input);
  if (!Number.isFinite(offset)) return fallback;
  return Math.max(ARTICLE_MEDIA_OFFSET_Y_MIN, Math.min(ARTICLE_MEDIA_OFFSET_Y_MAX, Math.round(offset)));
}

export function safeArticleImageFlowX(input: unknown, layout: ArticleImageLayout = "center") {
  const fallback = layout === "left" ? 0 : layout === "right" ? 100 : 50;
  const value = Number(input ?? fallback);
  if (!Number.isFinite(value)) return fallback;
  return Math.max(0, Math.min(100, Math.round(value)));
}

export function articleImageLayoutFromFlowX(flowX: number, currentLayout: ArticleImageLayout): ArticleImageLayout {
  if (currentLayout === "full" || currentLayout === "wide") return currentLayout;
  if (flowX <= 42) return "left";
  if (flowX >= 58) return "right";
  return "center";
}

export function articleImageFlowInset(width: number, flowX: number, layout: ArticleImageLayout) {
  if (layout === "left") return Math.max(0, ((100 - width) * flowX) / 100);
  if (layout === "right") return Math.max(0, ((100 - width) * (100 - flowX)) / 100);
  return 0;
}

export function articleImageEffectiveLayout(layout: unknown, wrap?: unknown): ArticleImageLayout {
  if (wrap === "float-left" || wrap === "shape-left") return "left";
  if (wrap === "float-right" || wrap === "shape-right") return "right";
  const safeLayout = safeArticleImageLayout(layout);
  return safeLayout === "wide" ? "center" : safeLayout;
}

export function articleImageClassName(layout: ArticleImageLayout, size: ArticleMediaSize, wrap?: string) {
  const effectiveLayout = articleImageEffectiveLayout(layout, wrap);
  const classLayout = layout === "wide" && !wrap ? "wide" : effectiveLayout;
  return [
    "article-media",
    "article-image",
    `article-image--${classLayout}`,
    `article-media--${size}`,
    wrap && wrap !== "none" ? `article-image--${wrap}` : "",
  ]
    .filter(Boolean)
    .join(" ");
}

export function articleEmbedClassName(layout: unknown, extraClassName = "") {
  const safeLayout = safeArticleMediaLayout(layout);
  return ["article-media", "article-embed", "inline-embed", `article-embed--${safeLayout}`, extraClassName].filter(Boolean).join(" ");
}

export function articleEmbedOuterClassName(layout: unknown, extraClassName = "") {
  const safeLayout = safeArticleMediaLayout(layout);
  return ["article-media", "article-embed", "inline-embed", "article-embed-shell", `article-embed-shell--${safeLayout}`, extraClassName].filter(Boolean).join(" ");
}

export function articleEmbedInnerClassName(extraClassName = "") {
  return ["article-embed-inner", extraClassName].filter(Boolean).join(" ");
}

export function articleImageLayoutStyle(input: {
  layout?: unknown;
  width?: unknown;
  flowX?: unknown;
  offsetX?: unknown;
  offsetY?: unknown;
}) {
  const layout = safeArticleImageLayout(input.layout);
  const width = typeof input.width === "number" && Number.isFinite(input.width) ? Math.max(30, Math.min(100, Math.round(input.width))) : 100;
  const flowX = safeArticleImageFlowX(input.flowX, layout);
  return {
    "--article-media-width": `${width}%`,
    "--article-media-inset": `${articleImageFlowInset(width, flowX, layout)}%`,
    "--article-media-offset-x": `${clampArticleMediaOffsetX(input.offsetX)}%`,
    "--article-media-offset-y": `${clampArticleMediaOffsetY(input.offsetY)}px`,
    ...(layout === "left" || layout === "right" ? {} : { width: `${width}%` }),
  };
}

export function articleEmbedLayoutStyle(input: {
  layout?: unknown;
  width?: unknown;
  size?: unknown;
  offsetX?: unknown;
  offsetY?: unknown;
}) {
  const layout = safeArticleMediaLayout(input.layout);
  const width = layout === "full" ? null : clampArticleMediaWidth(input.width, articleMediaWidthFromLegacySize(input.size));
  return {
    "--article-media-width": layout === "full" || width === null ? "100%" : `${width}px`,
    "--article-media-inset": "0%",
    "--article-media-offset-x": `${clampArticleMediaOffsetX(input.offsetX)}%`,
    "--article-media-offset-y": `${clampArticleMediaOffsetY(input.offsetY)}px`,
    ...(layout === "full" || width === null ? { width: "100%" } : { width }),
  };
}

export function articleEmbedOuterLayoutStyle(input: {
  layout?: unknown;
  width?: unknown;
  size?: unknown;
  offsetX?: unknown;
  offsetY?: unknown;
}) {
  const style = articleEmbedLayoutStyle(input);
  return {
    "--article-media-width": String(style["--article-media-width"]),
    "--article-media-inset": String(style["--article-media-inset"]),
    "--article-media-offset-x": String(style["--article-media-offset-x"]),
    "--article-media-offset-y": String(style["--article-media-offset-y"]),
    width: "100%",
  };
}

export function articleEmbedInnerLayoutStyle(input: {
  layout?: unknown;
  width?: unknown;
  size?: unknown;
}) {
  const layout = safeArticleMediaLayout(input.layout);
  const width = layout === "full" ? null : clampArticleMediaWidth(input.width, articleMediaWidthFromLegacySize(input.size));
  return {
    width: layout === "full" || width === null ? "100%" : `${width}px`,
    maxWidth: "100%",
    marginLeft: layout === "left" || layout === "full" ? "0" : "auto",
    marginRight: layout === "right" || layout === "full" ? "0" : "auto",
  };
}
